// Pure ledger model for the Payment Overview member modal: it maps a member's
// charges and payments onto each other, and flips the balance sign for display.

export interface EventDebt {
  eventId: string;
  eventName: string;
  eventDate: string;
  owed: number;
  paid: number;
  balance: number;
  /** The charge's intent, whatever its status. */
  intentId?: string;
  /** Present iff "Cancel charge" may target it: pending, nothing paid. */
  cancellableIntentId?: string;
  /** false = the member reported "not going"; the charge is still open. */
  attending?: boolean | null;
  /** Session called off: `owed` is what was received, not the price. */
  cancelled?: boolean;
  /** Money that arrived after the cancellation — refundable, not settled. */
  paidAfterCancellation?: number;
}

export interface MembershipDebt {
  membershipId: string;
  membershipName: string;
  periodStart: string;
  periodEnd: string;
  owed: number;
  paid: number;
  balance: number;
  intentId?: string;
  cancellableIntentId?: string;
}

export interface MemberBalance {
  memberId: string;
  memberName: string;
  groups: { _id: string; name: string }[];
  totalOwed: number;
  totalPaid: number;
  balance: number;
  eventDebts: EventDebt[];
  membershipDebts: MembershipDebt[];
}

export interface PaymentItem {
  _id: number;
  member: { _id: number; name: string } | null;
  event: { _id: number; name: string; startTime: string } | null;
  membership: { _id: number; name: string } | null;
  paymentIntentId?: number | null;
  date: string;
  amount: number;
  description: string;
}

export type ChargeKind = 'event' | 'membership';

export interface LedgerPayment {
  id: number;
  date: string;
  amount: number;
  /** Event / membership / free-text description, in that order of preference. */
  label: string;
  source: PaymentItem;
}

export interface LedgerCharge {
  /** React list key. Membership charges key on intentId — one membership can
      carry several live charges and membershipId collides between them. */
  key: string;
  kind: ChargeKind;
  sourceId: string | null;
  intentId?: string;
  name: string;
  /** Event start, or the membership period start. */
  dateFrom: string;
  /** Membership period end; empty for events. */
  dateTo: string;
  owed: number;
  paid: number;
  /** API sign: owed - paid. Flip with `toDisplayBalance` before rendering. */
  balance: number;
  cancellableIntentId?: string;
  attending?: boolean | null;
  /** Session called off: `owed` is what was received, not the price. */
  cancelled?: boolean;
  /** Money that arrived after the cancellation — refundable, not settled. */
  paidAfterCancellation?: number;
  payments: LedgerPayment[];
}

/** Tier 3: money that funds several live sibling charges at once. */
export interface LedgerSplit {
  key: string;
  kind: ChargeKind;
  sourceId: string;
  sourceName: string;
  chargeNames: string[];
  payments: LedgerPayment[];
}

export interface MemberLedger {
  charges: LedgerCharge[];
  splits: LedgerSplit[];
  /** Money with a source but no charge on screen: filtered out, cancelled, waived. */
  noMatchingCharge: LedgerPayment[];
  /** Money with no event and no membership at all — bank imports, mostly. */
  unallocated: LedgerPayment[];
  /** Every payment, newest first — the flat "Payments" tab. */
  allPayments: LedgerPayment[];
}

function sourceKey(kind: ChargeKind, id: string): string {
  return `${kind}:${id}`;
}

function toLedgerPayment(payment: PaymentItem): LedgerPayment {
  return {
    id: payment._id,
    date: payment.date,
    amount: Number(payment.amount),
    label: payment.event?.name || payment.membership?.name || payment.description || '',
    source: payment,
  };
}

function chargesOf(member: MemberBalance): LedgerCharge[] {
  const events: LedgerCharge[] = member.eventDebts.map((debt) => ({
    key: `event-${debt.eventId}`,
    kind: 'event',
    sourceId: debt.eventId,
    ...(debt.intentId != null ? { intentId: debt.intentId } : {}),
    ...(debt.cancellableIntentId != null ? { cancellableIntentId: debt.cancellableIntentId } : {}),
    name: debt.eventName,
    dateFrom: debt.eventDate,
    dateTo: '',
    owed: Number(debt.owed),
    paid: Number(debt.paid),
    balance: Number(debt.balance),
    attending: debt.attending ?? null,
    ...(debt.cancelled ? { cancelled: true } : {}),
    ...(debt.paidAfterCancellation != null
      ? { paidAfterCancellation: Number(debt.paidAfterCancellation) }
      : {}),
    payments: [],
  }));

  const memberships: LedgerCharge[] = member.membershipDebts.map((debt) => ({
    key: `membership-${debt.intentId ?? debt.membershipId}`,
    kind: 'membership',
    // An orphan intent carries `orphan-<intentId>`; it has no real source, so
    // no payment can be matched to it by source.
    sourceId: debt.membershipId.startsWith('orphan-') ? null : debt.membershipId,
    ...(debt.intentId != null ? { intentId: debt.intentId } : {}),
    ...(debt.cancellableIntentId != null ? { cancellableIntentId: debt.cancellableIntentId } : {}),
    name: debt.membershipName,
    dateFrom: debt.periodStart,
    dateTo: debt.periodEnd,
    owed: Number(debt.owed),
    paid: Number(debt.paid),
    balance: Number(debt.balance),
    payments: [],
  }));

  return [...events, ...memberships];
}

// Cascade: intent link, then a source with one live charge, then a source with
// several (split), then no source. Anything else: noMatchingCharge.
export function buildMemberLedger(member: MemberBalance, payments: PaymentItem[]): MemberLedger {
  const charges = chargesOf(member);

  const byIntent = new Map<string, LedgerCharge>();
  const bySource = new Map<string, LedgerCharge[]>();
  for (const charge of charges) {
    if (charge.intentId) byIntent.set(String(charge.intentId), charge);
    if (charge.sourceId) {
      const key = sourceKey(charge.kind, charge.sourceId);
      const list = bySource.get(key);
      if (list) list.push(charge);
      else bySource.set(key, [charge]);
    }
  }

  const allPayments = [...payments]
    .map(toLedgerPayment)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const splitMap = new Map<string, LedgerSplit>();
  const noMatchingCharge: LedgerPayment[] = [];
  const unallocated: LedgerPayment[] = [];

  for (const payment of allPayments) {
    const intentId = payment.source.paymentIntentId;
    if (intentId != null) {
      const linked = byIntent.get(String(intentId));
      // A linked payment funds THAT charge only; recalculate keeps it out of
      // every sibling's total, so source matching would misattribute it.
      if (linked) linked.payments.push(payment);
      else noMatchingCharge.push(payment);
      continue;
    }

    const kind: ChargeKind | null = payment.source.event
      ? 'event'
      : payment.source.membership ? 'membership' : null;
    const rawId = payment.source.event?._id ?? payment.source.membership?._id ?? null;
    if (kind == null || rawId == null) {
      unallocated.push(payment);
      continue;
    }

    const siblings = bySource.get(sourceKey(kind, String(rawId))) ?? [];
    if (siblings.length === 1) {
      siblings[0].payments.push(payment);
    } else if (siblings.length > 1) {
      const key = sourceKey(kind, String(rawId));
      const existing = splitMap.get(key);
      if (existing) {
        existing.payments.push(payment);
      } else {
        splitMap.set(key, {
          key,
          kind,
          sourceId: String(rawId),
          sourceName: payment.label || siblings[0].name,
          chargeNames: siblings.map((c) => c.name),
          payments: [payment],
        });
      }
    } else {
      // The source exists but shows no charge here — filtered out, cancelled or
      // waived. Not the same as having no source at all.
      noMatchingCharge.push(payment);
    }
  }

  return { charges, splits: [...splitMap.values()], noMatchingCharge, unallocated, allPayments };
}

/**
 * Mark-as-paid body. The intent id is what stops `recalculate` from spreading
 * the money across every live sibling charge on the same source.
 */
export function buildMarkPaidPayload(
  memberId: string,
  charge: LedgerCharge,
  now: Date = new Date(),
): Record<string, unknown> | null {
  if (!charge.sourceId || charge.balance <= 0) return null;
  return {
    member: memberId,
    amount: charge.balance,
    event: charge.kind === 'event' ? charge.sourceId : null,
    membership: charge.kind === 'membership' ? charge.sourceId : null,
    ...(charge.intentId ? { paymentIntent: charge.intentId } : {}),
    date: now.toISOString(),
    description: '',
  };
}

/** API balance is `owed - paid`; the screen shows `paid - owed`. */
export function toDisplayBalance(apiBalance: number): number {
  return apiBalance === 0 ? 0 : -apiBalance;
}

/** Feed this the DISPLAY value: negative owes (red), positive is a credit. */
export function getDisplayBalanceClass(displayBalance: number): string {
  if (displayBalance < 0) return 'payment-overview__balance--owes';
  if (displayBalance > 0) return 'payment-overview__balance--overpaid';
  return 'payment-overview__balance--settled';
}
